<template>
  <header>
    <nav class="navbar navbar-expand-md navbar-light">
      <div class="container-fluid align-items-sm-center justify-content-end p-0">
        <router-link to="/Home" class="navbar-brand">
          <img :src="$t('Header.BrandLogo')" alt="Brand Logo" class="img-fluid" />
        </router-link>

        <button
          class="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbar-Nav"
          @click="toggleCollapse"
          aria-controls="navbar-Nav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          :class="{ active: isCollapsed }"
        ></button>

        <div :class="{ show: isCollapsed }" class="collapse navbar-collapse justify-content-end my-2" id="navbar-Nav">
          <ul class="navbar-nav mr-auto">
            <li class="nav-item">
              <router-link to="/Home" class="nav-link text-uppercase" @click="closeMenu">
                {{ $t('Header.HomeTab') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/Upload" class="nav-link text-uppercase" @click="closeMenu">
                {{ $t('Header.UploadTab') }}
              </router-link>
            </li>
             <li class="nav-item">
              <router-link to="/BookLibrary" class="nav-link text-uppercase" @click="closeMenu">
                {{ $t('Header.LibraryTab') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/ParticipatingProducts" class="nav-link text-uppercase" @click="closeMenu">
                {{ $t('Header.ProductsTab') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/Support" class="nav-link text-uppercase" @click="closeMenu">
                {{ $t('Header.SupportTab') }}
              </router-link>
            </li>
          </ul>
        </div>

        <div class="langtoggle">
          <button @click="changeLanguage('en')" :class="{ active: currentLang === 'en' }">English</button>&nbsp;<span style="color: #fff;">|</span>&nbsp;
          <button @click="changeLanguage('fr')" :class="{ active: currentLang === 'fr' }">Français</button>
        </div>
      </div>
    </nav>
  </header>

  <banner></banner>
</template>


<script>
import banner from '../UI/Banner.vue';
import { useI18n } from 'vue-i18n';
import { ref, watch, onMounted } from 'vue';
import { loadLocaleMessages } from '@/main.js';

export default {
  components: { banner },
  setup() {
    const { locale, t } = useI18n();
    const currentLang = ref(locale.value);
    const isCollapsed = ref(false); // ✅ Define state for toggle

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const closeMenu = () => {
      isCollapsed.value = false;
    };

    const changeLanguage = async (lang) => {
      if (currentLang.value === lang) return;

  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost";
  const isStaging = hostname.includes("staging");

  if (isLocal) {
    // Local: Change language dynamically
    await loadLocaleMessages(lang);
    locale.value = lang;
    localStorage.setItem("locale", lang);
    currentLang.value = lang;
    injectCookieScript(lang);
    injectGTM();
  } else {
    // Staging & Production: Redirect to language-specific domains
    const currentPath = window.location.pathname;
    const currentQuery = window.location.search;

    const langBaseUrls = {
      en: isStaging ? "https://staging-feedingreadingen.snipp.us" : "https://feedingreading.ca",
      fr: isStaging ? "https://staging-feedingreadingfr.snipp.us" : "https://nourrirlalecture.ca",
    };

    const targetBase = langBaseUrls[lang] || "/";
    const targetUrl = `${targetBase}${currentPath}${currentQuery}`;

    window.location.href = targetUrl;
  }
};

    watch(locale, (newLocale) => {
      currentLang.value = newLocale;
    });

    onMounted(() => {
      injectGTM();
      injectCookieScript(currentLang.value);
    });

    const injectGTM = () => {
      const gtmId = t('gtmId');
      if (!gtmId) return;

      document.querySelectorAll("[data-gtm]").forEach(el => el.remove());

      const script = document.createElement("script");
      script.setAttribute("data-gtm", gtmId);
      script.innerHTML = `
        (function (w, d, s, l, i) {
          w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; 
          j.async = true; j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl; 
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${gtmId}');
      `;
      document.head.appendChild(script);

      const noscript = document.createElement("noscript");
      noscript.setAttribute("data-gtm", gtmId);
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.prepend(noscript);
    };

    const injectCookieScript = async (lang) => {
  try {
    const response = await fetch(`/locales/${lang}.json`);
    if (!response.ok) throw new Error(`Failed to load locale: ${lang}`);

    const data = await response.json();
    const cookieScript = data?.Header?.cookieScript;
    if (!cookieScript) return;

    // Remove old scripts
    document.querySelectorAll("[data-cookie-script]").forEach(el => el.remove());

    // Parse HTML and inject scripts properly
    const temp = document.createElement("div");
    temp.innerHTML = cookieScript;

    Array.from(temp.querySelectorAll("script, link")).forEach(tag => {
      const newTag = document.createElement(tag.tagName.toLowerCase());

      // Copy all attributes
      Array.from(tag.attributes).forEach(attr => {
        newTag.setAttribute(attr.name, attr.value);
      });

      // Copy inline script content
      if (tag.textContent) newTag.textContent = tag.textContent;

      newTag.setAttribute("data-cookie-script", lang);
      document.head.appendChild(newTag);
    });

  } catch (error) {
    console.error("CookieScript injection failed:", error);
  }
};

    return { currentLang, changeLanguage, isCollapsed, toggleCollapse, closeMenu };
  },
};
</script>
<style scoped>
header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9;
  padding: 1rem 0;
}

.navbar {
  padding: 0;
}

ul.navbar-nav {
  width: 100%;
}

#navbar-Nav>ul>li a {
  color: #fff;
  line-height: 1;
  padding: 0.7rem;
  text-transform: uppercase;
  font-weight: 600;
}

.navbar-brand {
  display: inline-block;
  padding-top: 0.3125rem;
  padding-bottom: 0.3125rem;
  margin-right: 1rem;
  font-size: 1.25rem;
  line-height: inherit;
  white-space: nowrap;
  width: 15%;
  margin-right: 2rem;
}

#navbar-Nav>ul>li+li {
  margin-left: 2%;
}

.list-inline>li {
  display: inline-block;
  padding-left: 5px;
  padding-right: 5px;
}

.social-icon {
  font-size: 2rem;
  border-radius: 5px;
  color: #f26a32;
}

header .navbar {
  padding: 1rem 1.5rem;
  justify-content: space-between;
}

.nav-link {
  background: transparent;
  cursor: pointer;
  color: #000;
  display: inline-block;
  text-decoration: none;
}

.nav-link:hover,
.nav-link.active {
  color: #d31245 !important;
}

.toogle-country-list {
  margin: 0;
}

.toogle-country-list ul {
  padding: 0;
  margin: 0;
}

.toogle-country-list ul li {
  position: relative;
}

.toogle-country-list ul li a {
  font-weight: 600;
  color: #fff;
  width: auto;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-decoration: none;
}

.toogle-country-list ul li a:hover {
  color: #d31245;
}

.toogle-country-list ul li:first-child:after {
  content: "|";
  color: #fff;
  position: absolute;
  right: 0;
  top: 0;
}

.toogle-country-list ul li:first-child {
  position: relative;
  margin: 0 10px 0 0;
  padding: 0 20px 0 0;
}

/* ==========================================================================
   mobile menu toggler Style
   ========================================================================== */
header .navbar .navbar-toggler {
  display: none;
  padding: 0px;
  border: 0;
  padding-right: 0;
}

.navbar-toggler {
  position: relative;
  top: 0;
  right: 0;
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #fff;
  z-index: 1;
  margin: 5px 10px;
}

.navbar-toggler::before {
  content: "";
  position: absolute;
  width: 35px;
  height: 5px;
  background: #fff;
  transform: translateY(-10px);
  box-shadow: 0 10px #fff;
  transition: 0.5s;
  border-radius: 20px;
}

.navbar-toggler::after {
  content: "";
  position: absolute;
  width: 35px;
  height: 5px;
  background: #fff;
  transform: translateY(10px);
  transition: 0.5s;
  border-radius: 20px;
}

.navbar-toggler.active::before {
  transform: translateY(0px) rotate(45deg);
  box-shadow: 0 0 #fff;
  background: #fff;
}

.navbar-toggler.active::after {
  transform: translateY(0px) rotate(-45deg);
  background: #fff;
}

@media screen and (max-width: 767px) {
  ul.navbar-nav {
    width: 100%;
  }

  header .navbar .navbar-toggler {
    display: flex;
    order: 3;
    z-index: 100;
  }

  header {
    padding: 0;
  }

  div#navbar-Nav {
    position: absolute;
    top: 0;
    left: 0;
    background: #513629;
    padding-top: 50px;
    text-align: left;
    width: 100%;
    margin: 0 !important;
  }

  #faqAccordion .accordion-button {
    font-size: 1.2rem;
  }

  header .navbar {
    padding: 0;
  }

  .navbar-brand {
    display: none;
  }

  ul.navbar-nav li {
    display: block;
    margin: 0 !important;
  }
}

@media screen and (max-width: 575px) {
  .navigation {
    margin-left: 0px !important;
  }

  .navigation img {
    max-width: 100%;
  }
}
</style>